// ── Translating a failure ─────────────────────────────────────────────────────
//
// The backend answers every error with a stable `code` from its catalogue, an
// English `message` for logs, an optional `details` object with the values a
// sentence needs, and optional per-field `fields` (§16).
//
// The UI translates the code. It falls back to the English message only for a
// code it has never heard of, which in a healthy build means never: a test
// fails the moment a code has no translation in all four languages.

const FALLBACK_KEY = 'errors.unexpected'

// The codes that only say what KIND of refusal it was. A module that answers
// one of these often says which one in `details` — `{ reason: 'ALREADY_CLOSED' }`,
// `{ code: 'REQUISITION_DECIDED' }` — and the generic sentence («Ese registro ya
// no está en el estado que esperabas») threw that away.
const GENERIC_CODES = new Set(['CONFLICT', 'FORBIDDEN', 'VALIDATION_ERROR', 'BAD_REQUEST', 'NOT_FOUND'])

/**
 * The specific reason a generic refusal carries, as an i18n key — or null.
 *
 * Under `errors.reasons.*` and not `errors.*`: a reason is a word one module
 * chose for its own `details`, and the top-level codes are another contract
 * (the platform catalogue). `ALREADY_VOID` is a fleet code meaning «that refuel
 * is void»; as a personnel reason it means a document version. Sharing one
 * branch would print the refuel sentence on a personnel screen.
 */
function reasonKey(error, t) {
  if (!GENERIC_CODES.has(error.code)) return null
  for (const candidate of [error.details?.reason, error.details?.code]) {
    if (typeof candidate !== 'string' || !/^[A-Z][A-Z0-9_]+$/.test(candidate)) continue
    const key = `errors.reasons.${candidate}`
    // vue-i18n returns the key itself when there is no translation.
    if (t(key) !== key) return key
  }
  return null
}

/**
 * The sentence to show the user.
 *
 * A generic code (CONFLICT, FORBIDDEN…) that says in `details.reason` or
 * `details.code` which refusal it is shows that one, when it has a sentence
 * under `errors.reasons.*`; otherwise the code's own sentence, as always.
 *
 * @param {Error|object} error  usually an ApiError
 * @param {Function} [t]        vue-i18n's translate
 * @returns {string}
 */
export function errorMessage(error, t) {
  if (!error) return t ? t(FALLBACK_KEY) : 'Something went wrong. Try again.'
  if (!t) return error.message || 'Something went wrong. Try again.'

  const reason = reasonKey(error, t)
  if (reason) return t(reason, error.details ?? {})

  const code = error.code
  if (code) {
    const key = `errors.${code}`
    // vue-i18n returns the key itself when there is no translation.
    const translated = t(key, error.details ?? {})
    if (translated !== key) return translated
  }

  return error.message || t(FALLBACK_KEY)
}

/**
 * Per-field messages, keyed by field name, for highlighting inputs.
 *
 *   const errors = fieldErrors(error, t)
 *   errors.email  // 'Introduce un correo válido'
 *
 * @returns {Record<string, string>}
 */
export function fieldErrors(error, t) {
  const fields = error?.fields
  if (!Array.isArray(fields) || !t) return {}

  const messages = {}
  for (const entry of fields) {
    const key = `errors.fields.${entry.code}`
    const translated = t(key, entry.details ?? {})
    messages[entry.field] = translated === key ? t('errors.fields.INVALID_VALUE') : translated
  }
  return messages
}

/**
 * True when the failure is the user's session rather than their input — the
 * shell handles those by redirecting, and a form should not also shout about it.
 */
export function isSessionError(error) {
  return ['SESSION_EXPIRED', 'TOKEN_EXPIRED', 'TOKEN_INVALID', 'REFRESH_TOKEN_INVALID', 'SESSION_IDLE_TIMEOUT']
    .includes(error?.code)
}

/**
 * The support line: an id the client can read out, which lands the technician
 * on the exact log entry. Only shown for failures that are ours (5xx).
 */
export function supportReference(error) {
  if (!error?.requestId) return null
  return error.status >= 500 || error.code === 'INTERNAL_ERROR' ? error.requestId : null
}
